/**
 * The Request Representation screen's controller: `representations.model.ts`
 * wired to `requestRepresentation`, `withdrawRepresentationRequest` and
 * `fetchRepresentationRequestsMade`.
 *
 * It owns the three form fields, the busy flag, the last outcome, and which
 * row is mid-withdrawal; the model decides what may be sent and what a row
 * says. The list is re-read after every write rather than patched, because
 * `canWithdraw` is the database's answer and not this screen's.
 *
 * The form is deliberately without an as-you-type check — see the note on the
 * hook for why a check here would be a code oracle where `AssignAgentDialog`'s
 * is not.
 */

import { useCallback, useEffect, useState } from "react";
import type { RepresentationRequestMade } from "@ondo/shared";

import {
  fetchRepresentationRequestsMade,
  requestRepresentation,
  withdrawRepresentationRequest,
} from "../lib/api.ts";
import {
  canSubmitRequest,
  describeRequestError,
  outcomeOf,
  toMadeList,
} from "./representations.model.ts";
import type { MadeListView, RequestFormView } from "./representations.model.ts";

export interface RequestRepresentationController {
  form: RequestFormView;
  made: MadeListView;
  /** Non-null while a withdrawal is in flight, so one row can be busy alone. */
  withdrawing: string | null;
  setCode: (value: string) => void;
  setConfirm: (value: string) => void;
  setNote: (value: string) => void;
  submit: () => void;
  withdraw: (requestId: string) => void;
}

/**
 * Asking a work's owner for a mandate.
 *
 * ONE WRITE AND ONE READ, and the write is not a search. There is no on-blur
 * check here, deliberately, and the contrast with `AssignAgentDialog` is the
 * whole reason to say so: that screen asks `POST /v1/eligibility` as the
 * collector types, because a collector holds the subject already and is owed a
 * usable answer about an id they were given. Here the caller holds nothing —
 * that is what the door is for — so a check-as-you-type would be a code oracle,
 * and the budget behind it is five refusals an hour rather than ten.
 *
 * @param principalOndoId the workspace asking. A firm on an organisation's
 * workspace and the person on a personal one, which is what decides who ends up
 * holding the mandate — and is checked at `admin` in SQL, so a junior member of
 * an advisory cannot commit the firm.
 */
export function useRequestRepresentationController(
  principalOndoId: string,
): RequestRepresentationController {
  const [code, setCode] = useState("");
  const [confirm, setConfirm] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<RequestFormView["outcome"]>(null);

  const [requests, setRequests] = useState<RepresentationRequestMade[] | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [withdrawing, setWithdrawing] = useState<string | null>(null);

  /**
   * Re-reads the list.
   *
   * Called after every write rather than the state being patched in place. The
   * server decides what a row says — `canWithdraw` comes off the same set the
   * withdraw admits — so a locally-edited copy would be this screen's opinion of
   * a permission the database owns.
   */
  const reload = useCallback(() => {
    let active = true;

    fetchRepresentationRequestsMade()
      .then((found) => {
        if (active) {
          setRequests(found);
          setListError(null);
        }
      })
      .catch((cause: unknown) => {
        if (active) setListError(describeRequestError(cause));
      });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => reload(), [reload]);

  const submit = useCallback(() => {
    if (!canSubmitRequest({ code, confirm, busy })) return;

    setBusy(true);
    setOutcome(null);

    requestRepresentation({
      code,
      confirm,
      note: note.trim() || null,
      asOndoId: principalOndoId,
    })
      .then((response) => {
        const result = outcomeOf(response);
        setOutcome(result);

        // Cleared only on success. A refused code stays in the field, because
        // the likeliest next act is correcting one character of it, and a form
        // that emptied itself would make the reader ask the collector again for
        // something they already have.
        if (result.kind === "sent") {
          setCode("");
          setConfirm("");
          setNote("");
          reload();
        }
      })
      .catch((cause: unknown) => {
        setOutcome({ kind: "refused", message: describeRequestError(cause) });
      })
      .finally(() => {
        setBusy(false);
      });
  }, [busy, code, confirm, note, principalOndoId, reload]);

  const withdraw = useCallback(
    (requestId: string) => {
      setWithdrawing(requestId);

      withdrawRepresentationRequest(requestId)
        .then(() => {
          reload();
        })
        .catch((cause: unknown) => {
          setListError(describeRequestError(cause));
        })
        .finally(() => {
          setWithdrawing(null);
        });
    },
    [reload],
  );

  return {
    form: {
      code,
      confirm,
      note,
      canSubmit: canSubmitRequest({ code, confirm, busy }),
      busy,
      outcome,
    },
    made: toMadeList({ requests, error: listError }),
    withdrawing,
    setCode,
    setConfirm,
    setNote,
    submit,
    withdraw,
  };
}
